import { message } from 'antd';
import type { AxiosProgressEvent } from 'axios';
import JSZip from 'jszip';
import { apiClient } from '../../../helpers/apiHelper';
import type { GeneralViewerInstance } from '../../../helpers/api/view_frame/view-frames';
import {
  closeExportProgressNotification,
  dismissExportProgressNotification,
  showExportProgressNotification,
} from './exportProgressNotification';

type StudyZipProgressEvent =
  | { stage: 'resolving'; current: number; total: number }
  | { stage: 'frames'; current: number; total: number }
  | { stage: 'zip'; percent: number };

export type StudyZipExportOptions = {
  delayMs?: number;
};

function pushStudyZipExportNotification(
  t: (k: string, o?: Record<string, unknown>) => string,
  e: StudyZipProgressEvent
): void {
  let percent = 0;
  let subtitle = '';
  if (e.stage === 'resolving') {
    percent = Math.round((e.current / Math.max(1, e.total)) * 18);
    subtitle = t('export.studyZipProgressResolving', { current: e.current, total: e.total });
  } else if (e.stage === 'frames') {
    percent = 18 + Math.round((e.current / Math.max(1, e.total)) * 72);
    percent = Math.min(90, percent);
    subtitle = t('export.studyZipProgressFrames', { current: e.current, total: e.total });
  } else {
    percent = 90 + Math.round((e.percent / 100) * 9);
    percent = Math.min(99, percent);
    subtitle = t('export.studyZipProgressZip', { percent: Math.round(e.percent) });
  }
  showExportProgressNotification({
    title: t('export.studyZipProgressTitle'),
    subtitle,
    percent,
  });
}

export function dataUrlToBlob(dataUrl: string): Blob | null {
  try {
    const comma = dataUrl.indexOf(',');
    if (comma < 0) return null;
    const header = dataUrl.slice(0, comma);
    const b64 = dataUrl.slice(comma + 1);
    const mime = header.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
    const bin = atob(b64);
    const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return new Blob([u8], { type: mime });
  } catch {
    return null;
  }
}

export function downloadFromDataUrl(dataUrl: string, filename: string): void {
  const blob = dataUrlToBlob(dataUrl);
  if (!blob) throw new Error('Invalid image data');
  downloadBlobFile(blob, filename);
}

export function downloadBlobFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Strip leading slash so paths join axios `baseURL` (…/api/v1). */
export function normalizeApiGetPath(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

export async function downloadDicomInstance(studyUID: string, inst: GeneralViewerInstance): Promise<void> {
  const q = new URLSearchParams({
    study_uid: studyUID,
    series_uid: inst.serie_instance_uid,
    object_uid: inst.sop_instance_uid,
  });
  const path = normalizeApiGetPath(`study_report/render/dicom-proxy?${q.toString()}`);
  const res: unknown = await apiClient.getBlob(path);
  const r = res as { data?: Blob } | Blob;
  const blob: Blob =
    r instanceof Blob
      ? r
      : (r as { data?: Blob })?.data instanceof Blob
        ? (r as { data: Blob }).data
        : new Blob([(r as { data?: unknown })?.data ?? r], { type: 'application/dicom' });
  downloadBlobFile(blob, `${inst.sop_instance_uid}.dcm`);
}

async function fetchFrameImageBlob(imageUrl: string): Promise<Blob | null> {
  try {
    const path = normalizeApiGetPath(imageUrl);
    const res: unknown = await apiClient.getBlob(path);
    const r = res as { data?: Blob } | Blob;
    if (r instanceof Blob) return r;
    if ((r as { data?: Blob })?.data instanceof Blob) return (r as { data: Blob }).data;
    return new Blob([(r as { data?: unknown })?.data ?? r], { type: 'image/jpeg' });
  } catch {
    return null;
  }
}

/** Re-encode a raster image blob (e.g. JPEG from the frame API) as PNG in the browser. */
export async function rasterImageBlobToPngBlob(imageBlob: Blob): Promise<Blob | null> {
  try {
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(imageBlob);
    } catch {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });
  } catch {
    return null;
  }
}

/** Throws with message `export.imageFailed` if the image cannot be fetched or saved. */
export async function downloadFrameJpegFromUrl(imageUrl: string, filename: string): Promise<void> {
  try {
    const blob = await fetchFrameImageBlob(imageUrl);
    if (!blob) throw new Error('export.imageFailed');
    downloadBlobFile(blob, filename);
  } catch (e) {
    if (e instanceof Error && e.message === 'export.imageFailed') throw e;
    throw new Error('export.imageFailed');
  }
}

const GROUPED_INSTANCE_PREFIX = '__grouped__';

function safeZipPathSegment(segment: string): string {
  return segment.replace(/[/\\:*?"<>|]/g, '_');
}

/** One folder per clip, zero-padded frame index (sortable). */
function frameZipEntryPath(inst: GeneralViewerInstance, frameIndex1Based: number, ext: 'jpg' | 'png'): string {
  return `${safeZipPathSegment(inst.sop_instance_uid)}/${String(frameIndex1Based).padStart(5, '0')}.${ext}`;
}

function studyZipBaseName(studyUID: string): string {
  return `study_${safeZipPathSegment(studyUID).slice(0, 80)}`;
}

async function sleep(ms: number): Promise<void> {
  await new Promise((r) => setTimeout(r, ms));
}

function extractFramesFromMetadataResponse(response: { data?: unknown }): { image_url?: string }[] {
  const top = response?.data;
  if (!top || typeof top !== 'object') return [];
  const payload =
    'data' in top && (top as { data?: unknown }).data && typeof (top as { data?: unknown }).data === 'object'
      ? ((top as { data: Record<string, unknown> }).data as Record<string, unknown>)
      : (top as Record<string, unknown>);
  const frames = payload?.frames;
  return Array.isArray(frames) ? (frames as { image_url?: string }[]) : [];
}

/** Build thumbnail URLs for every frame when the instance exposes a predictable pattern (video or DICOM). */
export function buildSequentialFrameThumbnailUrls(inst: GeneralViewerInstance): string[] {
  const fc = inst.frame_count ?? 0;
  if (fc <= 0 || !inst.first_frame_image_url) return [];
  if (inst.sop_instance_uid.startsWith(GROUPED_INSTANCE_PREFIX)) return [];

  const u = inst.first_frame_image_url;

  if (u.includes('video-thumbnail')) {
    const qIdx = u.indexOf('?');
    const params = new URLSearchParams(qIdx >= 0 ? u.slice(qIdx + 1) : '');
    const sop = params.get('sopInstanceUID') || inst.sop_instance_uid;
    if (!sop) return [];
    const out: string[] = [];
    for (let i = 0; i < fc; i++) {
      out.push(
        `/view-frame/video-thumbnail?sopInstanceUID=${encodeURIComponent(sop)}&frameNumber=${i}`
      );
    }
    return out;
  }

  if (u.includes('thumbnail')) {
    const qIdx = u.indexOf('?');
    if (qIdx < 0) return [];
    const params = new URLSearchParams(u.slice(qIdx + 1));
    const studyUID = params.get('studyUID');
    const seriesUID = params.get('seriesUID') ?? '';
    const sop = params.get('sopInstanceUID') || inst.sop_instance_uid;
    if (!studyUID || !sop) return [];
    const out: string[] = [];
    for (let i = 1; i <= fc; i++) {
      out.push(
        `/view-frame/thumbnail?studyUID=${encodeURIComponent(studyUID)}&seriesUID=${encodeURIComponent(seriesUID)}&sopInstanceUID=${encodeURIComponent(sop)}&frameNumber=${i}`
      );
    }
    return out;
  }

  return [];
}

async function fetchFrameImageUrlsFromMetadata(
  studyUID: string,
  sopInstanceUID: string
): Promise<string[]> {
  const path = `view-frame/${encodeURIComponent(studyUID)}/general-viewer/instances/${encodeURIComponent(sopInstanceUID)}/frames?metadata_only=true`;
  try {
    const response = await apiClient.get(path);
    return extractFramesFromMetadataResponse(response as { data?: unknown })
      .map((f) => (typeof f.image_url === 'string' ? f.image_url : null))
      .filter((x): x is string => !!x);
  } catch {
    return [];
  }
}

/** Resolve every frame image URL for one instance (grouped stacks and fallbacks use the metadata endpoint). */
export async function resolveAllFrameImageUrlsForInstance(
  studyUID: string,
  inst: GeneralViewerInstance
): Promise<string[]> {
  if (inst.sop_instance_uid.startsWith(GROUPED_INSTANCE_PREFIX)) {
    return fetchFrameImageUrlsFromMetadata(studyUID, inst.sop_instance_uid);
  }
  const built = buildSequentialFrameThumbnailUrls(inst);
  const fc = inst.frame_count ?? 0;
  if (built.length === fc && fc > 0) return built;
  if (fc > 0) {
    const fromMeta = await fetchFrameImageUrlsFromMetadata(studyUID, inst.sop_instance_uid);
    if (fromMeta.length) return fromMeta;
  }
  return built;
}

/** Exam list: all frames as one ZIP of JPEGs (folder per clip). */
export async function downloadAllFramesAsJpeg(
  studyUID: string,
  instances: GeneralViewerInstance[],
  t: (k: string, o?: Record<string, unknown>) => string,
  options?: StudyZipExportOptions
): Promise<void> {
  const delayMs = options?.delayMs ?? 260;

  showExportProgressNotification({
    title: t('export.studyZipProgressTitle'),
    subtitle: t('export.studyZipProgressStarting'),
    percent: 0,
  });

  const candidates = instances.filter((i) => (i.frame_count ?? 0) > 0);
  const plans: { inst: GeneralViewerInstance; urls: string[] }[] = [];
  for (let ri = 0; ri < candidates.length; ri++) {
    pushStudyZipExportNotification(t, {
      stage: 'resolving',
      current: ri + 1,
      total: Math.max(1, candidates.length),
    });
    const inst = candidates[ri];
    const urls = await resolveAllFrameImageUrlsForInstance(studyUID, inst);
    if (urls.length) plans.push({ inst, urls });
  }

  const total = plans.reduce((sum, p) => sum + p.urls.length, 0);
  if (!total) {
    dismissExportProgressNotification();
    message.warning(t('export.noFramePreviewUrls'));
    return;
  }

  try {
    const zip = new JSZip();
    let saved = 0;
    let failed = 0;
    let frameIx = 0;
    for (const { inst, urls } of plans) {
      for (let j = 0; j < urls.length; j++) {
        const blob = await fetchFrameImageBlob(urls[j]);
        if (!blob) {
          failed++;
        } else {
          zip.file(frameZipEntryPath(inst, j + 1, 'jpg'), blob);
          saved++;
        }
        frameIx++;
        pushStudyZipExportNotification(t, { stage: 'frames', current: frameIx, total });
        await sleep(delayMs);
      }
    }
    if (saved === 0) {
      dismissExportProgressNotification();
      message.warning(t('export.noFramePreviewUrls'));
      return;
    }
    const zipBlob = await zip.generateAsync(
      { type: 'blob', compression: 'STORE' },
      (meta) => pushStudyZipExportNotification(t, { stage: 'zip', percent: meta.percent ?? 0 })
    );
    downloadBlobFile(zipBlob, `${studyZipBaseName(studyUID)}_frames_jpeg.zip`);
    showExportProgressNotification({
      title: t('export.studyZipProgressTitle'),
      subtitle: t('export.studyZipProgressDone'),
      percent: 100,
    });
    closeExportProgressNotification();
    if (failed > 0) {
      message.warning(t('export.studyZipPartial', { saved, failed }));
    } else {
      message.success(t('export.studyZipReady', { count: saved }));
    }
  } catch {
    dismissExportProgressNotification();
    message.error(t('export.studyZipFailed'));
  }
}

/** Same sources, PNG re-encoded in the browser, single ZIP. */
export async function downloadAllFramesAsPng(
  studyUID: string,
  instances: GeneralViewerInstance[],
  t: (k: string, o?: Record<string, unknown>) => string,
  options?: StudyZipExportOptions
): Promise<void> {
  const delayMs = options?.delayMs ?? 280;

  showExportProgressNotification({
    title: t('export.studyZipProgressTitle'),
    subtitle: t('export.studyZipProgressStarting'),
    percent: 0,
  });

  const candidates = instances.filter((i) => (i.frame_count ?? 0) > 0);
  const plans: { inst: GeneralViewerInstance; urls: string[] }[] = [];
  for (let ri = 0; ri < candidates.length; ri++) {
    pushStudyZipExportNotification(t, {
      stage: 'resolving',
      current: ri + 1,
      total: Math.max(1, candidates.length),
    });
    const inst = candidates[ri];
    const urls = await resolveAllFrameImageUrlsForInstance(studyUID, inst);
    if (urls.length) plans.push({ inst, urls });
  }

  const total = plans.reduce((sum, p) => sum + p.urls.length, 0);
  if (!total) {
    dismissExportProgressNotification();
    message.warning(t('export.noFramePreviewUrls'));
    return;
  }

  try {
    const zip = new JSZip();
    let saved = 0;
    let failed = 0;
    let frameIx = 0;
    for (const { inst, urls } of plans) {
      for (let j = 0; j < urls.length; j++) {
        const blob = await fetchFrameImageBlob(urls[j]);
        if (!blob) {
          failed++;
        } else {
          const png = await rasterImageBlobToPngBlob(blob);
          if (!png) {
            failed++;
          } else {
            zip.file(frameZipEntryPath(inst, j + 1, 'png'), png);
            saved++;
          }
        }
        frameIx++;
        pushStudyZipExportNotification(t, { stage: 'frames', current: frameIx, total });
        await sleep(delayMs);
      }
    }
    if (saved === 0) {
      dismissExportProgressNotification();
      message.warning(t('export.noFramePreviewUrls'));
      return;
    }
    const zipBlob = await zip.generateAsync(
      { type: 'blob', compression: 'DEFLATE' },
      (meta) => pushStudyZipExportNotification(t, { stage: 'zip', percent: meta.percent ?? 0 })
    );
    downloadBlobFile(zipBlob, `${studyZipBaseName(studyUID)}_frames_png.zip`);
    showExportProgressNotification({
      title: t('export.studyZipProgressTitle'),
      subtitle: t('export.studyZipProgressDone'),
      percent: 100,
    });
    closeExportProgressNotification();
    if (failed > 0) {
      message.warning(t('export.studyZipPartial', { saved, failed }));
    } else {
      message.success(t('export.studyZipReady', { count: saved }));
    }
  } catch {
    dismissExportProgressNotification();
    message.error(t('export.studyZipFailed'));
  }
}

export async function downloadAllDicomInstances(
  studyUID: string,
  instances: GeneralViewerInstance[],
  t: (k: string, o?: Record<string, unknown>) => string,
  delayMs = 320
): Promise<void> {
  if (!instances.length) return;
  const n = instances.length;
  showExportProgressNotification({
    title: t('export.progressAllDicom'),
    subtitle: t('export.progressDicomFile', { current: 0, total: n }),
    percent: 0,
  });
  try {
    for (let i = 0; i < n; i++) {
      try {
        await downloadDicomInstance(studyUID, instances[i]);
      } catch {
        message.error(t('export.dicomFailed'));
      }
      const pct = Math.round(((i + 1) / n) * 100);
      showExportProgressNotification({
        title: t('export.progressAllDicom'),
        subtitle: t('export.progressDicomFile', { current: i + 1, total: n }),
        percent: pct,
      });
      await new Promise((r) => setTimeout(r, delayMs));
    }
    showExportProgressNotification({
      title: t('export.progressAllDicom'),
      subtitle: t('export.progressDicomFile', { current: n, total: n }),
      percent: 100,
    });
    closeExportProgressNotification();
  } catch {
    dismissExportProgressNotification();
  }
}

function updateBlobDownloadProgress(
  t: (k: string, o?: Record<string, unknown>) => string,
  titleKey: string,
  e: AxiosProgressEvent
): void {
  const total = e.total;
  if (total && total > 0) {
    const p = Math.round((e.loaded / total) * 100);
    showExportProgressNotification({
      title: t(titleKey),
      percent: p,
    });
  } else {
    showExportProgressNotification({
      title: t(titleKey),
      percent: null,
    });
  }
}

export async function downloadReportPdf(
  studyUID: string,
  includeImages: boolean,
  filename: string,
  t: (k: string, o?: Record<string, unknown>) => string
): Promise<void> {
  showExportProgressNotification({
    title: t('export.progressReportPdf'),
    percent: null,
  });
  try {
    const response = await apiClient.downloadFile(`report-sharing/${studyUID}`, {
      params: { include_images: includeImages },
      onDownloadProgress: (e) => updateBlobDownloadProgress(t, 'export.progressReportPdf', e),
    });
    const blob: Blob = response.data;
    showExportProgressNotification({ title: t('export.progressReportPdf'), percent: 100 });
    downloadBlobFile(blob, filename);
    closeExportProgressNotification();
  } catch (e) {
    dismissExportProgressNotification();
    throw e;
  }
}

export type ViewThumbnailUrlRow = {
  thumbnail_url: string;
  frame_number: number;
  frame_id?: string;
};

const VIEW_THUMB_COUNT_CAP = 5000;

/** SOP instance UID from a `/view-frame/thumbnail?…` or `video-thumbnail?…` path. */
export function parseSopInstanceFromThumbnailUrl(thumbnailPathOrUrl: string): string | null {
  const q = thumbnailPathOrUrl.indexOf('?');
  if (q < 0) return null;
  const params = new URLSearchParams(thumbnailPathOrUrl.slice(q + 1));
  return params.get('sopInstanceUID');
}

export async function fetchViewThumbnailUrlRows(
  studyUID: string,
  viewCode: string,
  count: number
): Promise<ViewThumbnailUrlRow[]> {
  const safeCount = Math.min(Math.max(1, count), VIEW_THUMB_COUNT_CAP);
  const response = await apiClient.get(
    `view-frame/${encodeURIComponent(studyUID)}/views/${encodeURIComponent(viewCode)}/thumbnail-urls?count=${safeCount}`
  );
  const top = (response as { data?: unknown })?.data;
  const raw =
    top && typeof top === 'object' && 'data' in top && Array.isArray((top as { data: unknown }).data)
      ? (top as { data: unknown[] }).data
      : Array.isArray(top)
        ? top
        : [];
  return raw
    .map((r: Record<string, unknown>) => ({
      thumbnail_url: String(r.thumbnail_url ?? ''),
      frame_number: Number(r.frame_number ?? 0),
      frame_id: r.frame_id != null ? String(r.frame_id) : undefined,
    }))
    .filter((r) => r.thumbnail_url.length > 0);
}

function viewZipFramePath(viewCode: string, frameNumber: number, ext: 'jpg' | 'png'): string {
  return `${safeZipPathSegment(viewCode)}/${String(frameNumber).padStart(5, '0')}.${ext}`;
}

function viewFramesZipBaseName(studyUID: string, viewCode: string, suffix: string): string {
  const base = `${studyZipBaseName(studyUID)}_${safeZipPathSegment(viewCode)}_${suffix}`;
  return base.length > 180 ? base.slice(0, 180) : base;
}

/** All frames for one anatomical view (thumbnail API), single JPEG ZIP. */
export async function downloadViewFramesAsJpegZip(
  studyUID: string,
  viewCode: string,
  frameCount: number,
  t: (k: string, o?: Record<string, unknown>) => string,
  options?: StudyZipExportOptions
): Promise<void> {
  const delayMs = options?.delayMs ?? 260;

  showExportProgressNotification({
    title: t('export.studyZipProgressTitle'),
    subtitle: t('export.studyZipProgressStarting'),
    percent: 0,
  });

  pushStudyZipExportNotification(t, { stage: 'resolving', current: 1, total: 1 });
  const rows = await fetchViewThumbnailUrlRows(studyUID, viewCode, frameCount);
  if (!rows.length) {
    dismissExportProgressNotification();
    message.warning(t('export.noFramePreviewUrls'));
    return;
  }

  try {
    const zip = new JSZip();
    let saved = 0;
    let failed = 0;
    const frameTotal = rows.length;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const blob = await fetchFrameImageBlob(row.thumbnail_url);
      if (!blob) {
        failed++;
      } else {
        zip.file(viewZipFramePath(viewCode, row.frame_number, 'jpg'), blob);
        saved++;
      }
      pushStudyZipExportNotification(t, { stage: 'frames', current: i + 1, total: frameTotal });
      await sleep(delayMs);
    }
    if (saved === 0) {
      dismissExportProgressNotification();
      message.warning(t('export.noFramePreviewUrls'));
      return;
    }
    const zipBlob = await zip.generateAsync(
      { type: 'blob', compression: 'STORE' },
      (meta) => pushStudyZipExportNotification(t, { stage: 'zip', percent: meta.percent ?? 0 })
    );
    downloadBlobFile(zipBlob, `${viewFramesZipBaseName(studyUID, viewCode, 'frames_jpeg')}.zip`);
    showExportProgressNotification({
      title: t('export.studyZipProgressTitle'),
      subtitle: t('export.studyZipProgressDone'),
      percent: 100,
    });
    closeExportProgressNotification();
    if (failed > 0) {
      message.warning(t('export.studyZipPartial', { saved, failed }));
    } else {
      message.success(t('export.studyZipReady', { count: saved }));
    }
  } catch {
    dismissExportProgressNotification();
    message.error(t('export.studyZipFailed'));
  }
}

/** Same sources, PNG re-encoded in the browser. */
export async function downloadViewFramesAsPngZip(
  studyUID: string,
  viewCode: string,
  frameCount: number,
  t: (k: string, o?: Record<string, unknown>) => string,
  options?: StudyZipExportOptions
): Promise<void> {
  const delayMs = options?.delayMs ?? 280;

  showExportProgressNotification({
    title: t('export.studyZipProgressTitle'),
    subtitle: t('export.studyZipProgressStarting'),
    percent: 0,
  });

  pushStudyZipExportNotification(t, { stage: 'resolving', current: 1, total: 1 });
  const rows = await fetchViewThumbnailUrlRows(studyUID, viewCode, frameCount);
  if (!rows.length) {
    dismissExportProgressNotification();
    message.warning(t('export.noFramePreviewUrls'));
    return;
  }

  try {
    const zip = new JSZip();
    let saved = 0;
    let failed = 0;
    const frameTotal = rows.length;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const blob = await fetchFrameImageBlob(row.thumbnail_url);
      if (!blob) {
        failed++;
      } else {
        const png = await rasterImageBlobToPngBlob(blob);
        if (!png) {
          failed++;
        } else {
          zip.file(viewZipFramePath(viewCode, row.frame_number, 'png'), png);
          saved++;
        }
      }
      pushStudyZipExportNotification(t, { stage: 'frames', current: i + 1, total: frameTotal });
      await sleep(delayMs);
    }
    if (saved === 0) {
      dismissExportProgressNotification();
      message.warning(t('export.noFramePreviewUrls'));
      return;
    }
    const zipBlob = await zip.generateAsync(
      { type: 'blob', compression: 'DEFLATE' },
      (meta) => pushStudyZipExportNotification(t, { stage: 'zip', percent: meta.percent ?? 0 })
    );
    downloadBlobFile(zipBlob, `${viewFramesZipBaseName(studyUID, viewCode, 'frames_png')}.zip`);
    showExportProgressNotification({
      title: t('export.studyZipProgressTitle'),
      subtitle: t('export.studyZipProgressDone'),
      percent: 100,
    });
    closeExportProgressNotification();
    if (failed > 0) {
      message.warning(t('export.studyZipPartial', { saved, failed }));
    } else {
      message.success(t('export.studyZipReady', { count: saved }));
    }
  } catch {
    dismissExportProgressNotification();
    message.error(t('export.studyZipFailed'));
  }
}

/** DICOM files for every clip that has at least one frame in this view (from thumbnail metadata). */
export async function downloadViewReferencedDicomInstances(
  studyUID: string,
  viewCode: string,
  frameCount: number,
  instances: GeneralViewerInstance[],
  t: (k: string, o?: Record<string, unknown>) => string,
  delayMs = 320
): Promise<void> {
  const rows = await fetchViewThumbnailUrlRows(studyUID, viewCode, frameCount);
  const sops = new Set<string>();
  for (const r of rows) {
    const sop = parseSopInstanceFromThumbnailUrl(r.thumbnail_url);
    if (sop) sops.add(sop);
  }
  const toDownload = instances.filter(
    (i) => (i.format_file ?? 'DICOM') === 'DICOM' && sops.has(i.sop_instance_uid)
  );
  if (!toDownload.length) {
    message.warning(t('export.viewDicomNone'));
    return;
  }
  await downloadAllDicomInstances(studyUID, toDownload, t, delayMs);
}

